import store from 'store'
import appsBuilderAPI from '@/api/apps-builder'

/** Lets guards finish when remote + localStorage both fail; BrowseHome tolerates empty segments. */
const FALLBACK_EMPTY_CONTENT_TREE = {
  contentTree: { segments: { def: [] } },
  segments: {},
  playlistGroups: {},
  playlists: {},
  videos: {}
}

const state = {
  isLoaded: false,
  source: 'none',
  data: {}
}

const DEFAULT_AGE_CATEGORY = 'def'
const SUPPORTED_SEGMENT_TYPES = ['smile_wagon', 'character_wagon', 'featured_wagon']

const getters = {
  findSegment(state) {
    return (segmentId) => {
      if (!state.data) return []

      return state.data.segments[segmentId]
    }
  },
  findPlaylistGroup(state) {
    return (playlistGroupId) => {
      if (!state.data) return []

      return state.data.playlistGroups[playlistGroupId]
    }
  },
  findPlaylist(state) {
    return (playlistId) => {
      if (!state.data) return []

      return state.data.playlists[playlistId]
    }
  },
  findVideo(state) {
    return (videoId) => {
      if (!state.data) return []

      return state.data.videos[videoId]
    }
  },
  findSegments(state) {
    return (ageCategory = DEFAULT_AGE_CATEGORY) => {
      if (!state.data || !state.data.contentTree || !state.data.contentTree.segments) return []

      return state.data.contentTree.segments[ageCategory]
        .map((segmentId) => state.data.segments[segmentId])
        .filter((segment) => SUPPORTED_SEGMENT_TYPES.includes(segment.segmentType))
    }
  },
  findPlaylistGroupsFromSegment(state) {
    return (segmentId, ageCategory = DEFAULT_AGE_CATEGORY) => {
      if (!state.data) return []

      const segment = state.data.segments[segmentId]

      if (!segment) return []

      const playlistGroupIds = segment.playlistGroups[ageCategory] || []

      return playlistGroupIds.map((playlistGroupId) => state.data.playlistGroups[playlistGroupId])
    }
  },
  findPlaylistsFromPlaylistGroup(state) {
    return (playlistGroupId) => {
      if (!state.data) return []

      const playlistGroup = state.data.playlistGroups[playlistGroupId]

      if (!playlistGroup) return []

      const playlistIds = playlistGroup.playlists || []

      return playlistIds.map((playlistId) => state.data.playlists[playlistId])
    }
  },
  findPlaylistsFromSegment(_, getters) {
    return (segmentId, ageCategory = DEFAULT_AGE_CATEGORY) => {
      const playlistGroups = getters.findPlaylistGroupsFromSegment(segmentId, ageCategory)

      return playlistGroups.flatMap((playlistGroup) => getters.findPlaylistsFromPlaylistGroup(playlistGroup.id))
    }
  },
  findVideosFromPlaylist(state) {
    return (playlistId) => {
      if (!state.data) return []

      const playlist = state.data.playlists[playlistId]

      if (!playlist) return []

      const playables = playlist.playables || []
      const videoIds = playables.filter((playable) => playable.contentType === 'VIDEO').map((playable) => playable.contentId)

      return videoIds.map((videoId) => state.data.videos[videoId])
    }
  },
  findVideosFromPlaylistGroup(_, getters) {
    return (playlistGroupId) => {
      const playlists = getters.findPlaylistsFromPlaylistGroup(playlistGroupId)

      return playlists.flatMap((playlist) => getters.findVideosFromPlaylist(playlist.id))
    }
  },
  findVideosFromSegment(_, getters) {
    return (segmentId, ageCategory = DEFAULT_AGE_CATEGORY) => {
      const playlistGroups = getters.findPlaylistGroupsFromSegment(segmentId, ageCategory)

      return playlistGroups.flatMap((playlistGroup) => getters.findVideosFromPlaylistGroup(playlistGroup.id))
    }
  }
}

const actions = {
  async load({ dispatch, commit }, countryCode) {
    const localContentTree = store.get('playkids/content-tree')

    if (localContentTree) {
      commit('SET_CONTENT_TREE', localContentTree)
      commit('SET_LOADED')
      commit('SET_SOURCE', 'cache')
    }

    await dispatch('fetchContentTree', countryCode.toLowerCase())
  },
  async fetchContentTree({ commit, state }, countryCode) {
    commit('SET_FETCHING', true)
    try {
      const contentTree = await appsBuilderAPI.createPkWebJson(0)

      commit('SET_CONTENT_TREE', contentTree)
      commit('SET_LOADED')
      commit('SET_SOURCE', 'remote')

      store.set('playkids/content-tree', contentTree)
    } catch {
      const cached = store.get('playkids/content-tree')
      if (!state.isLoaded && cached) {
        commit('SET_CONTENT_TREE', cached)
        commit('SET_LOADED')
        commit('SET_SOURCE', 'cache')
      }
    } finally {
      commit('SET_FETCHING', false)
    }

    if (!state.isLoaded) {
      commit('SET_CONTENT_TREE', FALLBACK_EMPTY_CONTENT_TREE)
      commit('SET_LOADED')
      commit('SET_SOURCE', 'none')
    }
  }
}

const mutations = {
  SET_CONTENT_TREE(state, contentTree) {
    state.data = contentTree
  },
  SET_LOADED(state) {
    state.isLoaded = true
  },
  SET_FETCHING(state, fetching) {
    state.fetching = fetching
  },
  SET_SOURCE(state, source) {
    state.source = source
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
